/**
 * Dashboard Header Section Component
 * Displays user profile, welcome message, and quick actions
 */
import { User, Shield, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

interface HeaderSectionProps {
  user: any;
  currentDP: any;
  photosLoading: boolean;
  onFindMatchesClick: () => void;
}

export default function HeaderSection({ user, currentDP, photosLoading, navigate, onFindMatchesClick }: HeaderSectionProps) {
  return (
    <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            {/* User Profile Picture with Badge */}
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 p-1 shadow-lg">
                <div className="w-full h-full bg-white rounded-xl overflow-hidden">
                  {currentDP && !photosLoading ? (
                    <img 
                      src={currentDP.src} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.log('❌ Profile DP failed to load:', currentDP.src);
                        e.currentTarget.src = 'https://via.placeholder.com/80x80?text=DP';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                      {photosLoading ? (
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <User className="w-8 h-8 text-blue-500" />
                      )}
                    </div>
                  )}
                </div>
              </div>
              <Badge className="absolute -top-2 -right-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white border-2 border-white shadow-lg">
                Active
              </Badge>
            </div>

            {/* User Info */}
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Welcome back, {user?.firstName || 'User'}!
              </h1>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-2 text-slate-600">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{user?.profession || 'Add Profession'}</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-600">
                  <Shield className="w-4 h-4" />
                  <span className="font-mono text-sm">ID: {user?.userId || 'Loading...'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center space-x-3">
            <Button 
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg"
              onClick={onFindMatchesClick}
            >
              <Search className="w-4 h-4 mr-2" />
              Find Matches
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}